import React, { useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import EmbeddedClinicList from "../../components/EmbeddedClinicList";
import { SectionBottomImage } from "../../components/ContentLayouts";

// ── Image imports ───────────────────────────────────────────────────────────────
import imgEcgByPQ from "../../assets/images/content/pet-ecg/ecg-bypq.png";

const faqs = [
  {
    question: "What is an ECG test for pets?",
    answer:
      "An ECG, or electrocardiogram, is a test that records your pet's heart rhythm and electrical activity. It helps vets spot irregular heartbeats or other signs of heart trouble.",
  },
  {
    question: "When does my pet need an ECG?",
    answer:
      "Vets usually recommend an ECG if your pet shows an irregular heartbeat, fainting episodes, breathing difficulty, unusual tiredness, or before a surgical procedure that requires anesthesia.",
  },
  {
    question: "Is an ECG painful for pets?",
    answer:
      "No. The test only involves placing electrodes on the skin to read electrical signals. There's no pain, no needles, and typically no sedation involved.",
  },
  {
    question: "What heart conditions can an ECG detect?",
    answer:
      "An ECG can help identify irregular heart rhythms, an abnormally fast or slow heartbeat, and other electrical abnormalities that may point to underlying heart conditions.",
  },
  {
    question: "How long does an ECG test take?",
    answer:
      "Most pet ECGs are completed within a few minutes. It's one of the quicker diagnostic tests your pet may go through during a vet visit.",
  },
  {
    question: "How much does a cat and dog ECG cost in Bangalore?",
    answer:
      "Costs can vary by clinic and location. Through PetQura, you can compare nearby clinics offering pet ECG services and choose one that fits your budget.",
  },
  {
    question: "Can I find a pet ECG near me in Bangalore through PetQura?",
    answer:
      "Yes. Simply select the ECG service on PetQura and enter your location. We'll show you nearby clinics offering the test, along with their availability.",
  },
  {
    question: "Do I need to prepare my pet before an ECG?",
    answer:
      "Generally, no special preparation is needed. Your vet will guide you if anything specific is required based on your pet's health or the clinic's process.",
  },
];

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <FaqRow>
      <FaqQuestion onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <FaqIcon>{open ? "−" : "+"}</FaqIcon>
      </FaqQuestion>
      {open && <FaqAnswer>{answer}</FaqAnswer>}
    </FaqRow>
  );
};

const PetEcg = () => {
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Pet ECG in Bangalore | PetQura</title>
        <meta
          name="description"
          content="Find pet ECG services near you in Bangalore. Connect with certified veterinary clinics offering heart health diagnostics for dogs and cats and book online instantly."
        />
        <link
          rel="canonical"
          href="https://petqura.com/services/pet-ecg-bangalore"
        />
      </Helmet>

      <PageWrapper>
        <HeroSection>
          <h1>Pet ECG Services Near You in Bangalore</h1>
          <EmbeddedClinicList serviceId={null} previewMode />
          <p>
            If you've typed <em>pet ECG near me bangalore</em> into your phone
            because your dog's breathing seemed off, we understand why you're
            here. An ECG, or electrocardiogram, is a simple test that checks
            your pet's heart rhythm and electrical activity, telling a vet
            whether the heart is beating the way it should or whether
            something needs a closer look. At PetQura, we help you find the
            nearest clinic offering this test, based on your location within
            Bangalore, so you can book an appointment quickly instead of
            calling clinic after clinic. Our goal is simple: take the
            guesswork out of finding the right care, so you can focus on your
            pet, not on the search.
          </p>
          <SectionBottomImage
            src={imgEcgByPQ}
            alt="Pet ECG services by PetQura in Bangalore"
          />
        </HeroSection>

        {/* ── When Needed ── */}
        <ContentSection>
          <SectionHeading>When Your Pet May Need an ECG</SectionHeading>
          <p>
            Hearts don't always show trouble in obvious ways. Here are a few
            situations where a vet might recommend an ECG for your cat or dog:
          </p>
          <BulletList>
            <li>
              Irregular heartbeat – skipped beats, racing pulse, or a rhythm
              that feels "off" during a routine check
            </li>
            <li>
              Fainting or collapsing episodes – sudden weakness or loss of
              consciousness, even briefly
            </li>
            <li>
              Difficulty breathing – laboured breathing, wheezing, or tiring
              quickly during normal activity
            </li>
            <li>
              Pre-surgery screening – many vets recommend an ECG before
              anesthesia to confirm the heart can handle the procedure safely
            </li>
            <li>
              Lethargy or reduced stamina – noticeably less energy than usual,
              especially in senior pets
            </li>
            <li>
              Existing heart conditions – ongoing monitoring for pets already
              diagnosed with a cardiac issue
            </li>
          </BulletList>
          <p>
            If your pet shows any of these signs, it's worth getting checked
            rather than waiting.
          </p>
        </ContentSection>

        {/* ── What to Expect ── */}
        <ContentSection>
          <SectionHeading>What to Expect During a Pet ECG</SectionHeading>
          <p>
            A pet ECG is quick and non-invasive, which usually comes as a
            relief to worried pet parents. The vet gently places a few small
            electrodes on your pet's skin, and these pick up the heart's
            electrical signals as it beats. Your pet stays awake and
            comfortable throughout, with no need for sedation in most cases,
            and the whole test typically takes just a few minutes. Once it's
            done, the vet reviews the recorded pattern to check for anything
            unusual, such as irregular rhythms or signs of strain. It's one of
            the more straightforward tests your pet will go through, giving
            vets a clear picture of heart health without any fuss.
          </p>
        </ContentSection>

        {/* ── Safety ── */}
        <ContentSection>
          <SectionHeading>How Safe Is an ECG for Pets?</SectionHeading>
          <p>
            Safety is usually the first question on every pet parent's mind,
            and rightly so. An ECG involves no needles, no radiation, and no
            sedatives in most cases. The electrodes only read electrical
            signals from the skin's surface; they don't send any current into
            your pet's body. This makes the test gentle enough for puppies,
            kittens, senior pets, and even anxious animals, with a trained vet
            supervising the entire process. For most pets, the biggest
            challenge is simply staying still for a few minutes, not the test
            itself.
          </p>
        </ContentSection>

        {/* ── Finding Near You ── */}
        <ContentSection>
          <SectionHeading>
            Finding a Pet ECG Near You in Bangalore
          </SectionHeading>
          <p>
            Bangalore is a big city, and traffic alone can turn a simple vet
            visit into a stressful ordeal for both you and your pet. That's
            exactly the problem we built PetQura to solve. Instead of
            searching separately for a <em>pet ECG near you bangalore</em>,
            you simply open PetQura, select the ECG service, and enter your
            location. We show you a list of nearby clinics offering cat and
            dog ECG testing, along with distance, timings, and available
            slots, so you choose what works best and you're done. No endless
            phone calls, no uncertainty about whether a clinic even offers the
            test you need.
          </p>
        </ContentSection>

        {/* ── Why Choose PetQura ── */}
        <ContentSection>
          <SectionHeading>Why Pet Parents Choose PetQura</SectionHeading>
          <p>
            We built PetQura around one idea: booking a vet visit shouldn't
            add to your stress.
          </p>
          <BulletList>
            <li>
              Location-based search – find clinics offering cat and dog ECG
              closest to where you are
            </li>
            <li>
              Verified clinic listings – see relevant details before you
              decide where to go
            </li>
            <li>
              Faster appointment booking – skip the back-and-forth calls and
              book directly
            </li>
            <li>
              Wide coverage across Bangalore – whether you're in the city
              centre or further out, we help you find options nearby
            </li>
            <li>
              Less stress for you and your pet – shorter travel time means a
              calmer, quicker visit
            </li>
          </BulletList>
        </ContentSection>

        {/* ── How to Book ── */}
        <ContentSection>
          <SectionHeading>
            How to Book Your Pet's ECG With PetQura
          </SectionHeading>
          <p>
            Booking through PetQura is meant to be quick, not another task on
            your to-do list: open PetQura and select the service you need, in
            this case a pet ECG, enter your location so we can find clinics
            nearest to you, compare the nearby options based on distance and
            availability, then choose a slot and confirm your appointment.
            That's it. From search to booking, we've kept every step simple,
            because when your pet isn't feeling their best, simple is what you
            need most.
          </p>
        </ContentSection>

        {/* ── FAQ ── */}
        <ContentSection>
          <SectionHeading>Frequently Asked Questions (FAQs)</SectionHeading>
          <FaqList>
            {faqs.map((faq, idx) => (
              <FaqItem key={idx} question={faq.question} answer={faq.answer} />
            ))}
          </FaqList>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default PetEcg;

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 5% 5rem;

  @media (max-width: 768px) {
    padding: 2rem 5% 4rem;
  }
`;

const HeroSection = styled.section`
  margin-bottom: 3rem;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 1.5rem;
    font-family: Montserrat, sans-serif;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const ContentSection = styled.section`
  margin-bottom: 2.5rem;

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 1rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1rem;
  font-family: Montserrat, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BulletList = styled.ul`
  padding-left: 1.4rem;
  margin-bottom: 1rem;

  li {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 0.6rem;
  }
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
`;

const FaqRow = styled.div`
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: none;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  font-family: Montserrat, sans-serif;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f7f9ff;
  }
`;

const FaqIcon = styled.span`
  font-size: 20px;
  font-weight: 400;
  color: #00419d;
  flex-shrink: 0;
  margin-left: 1rem;
`;

const FaqAnswer = styled.div`
  padding: 0 1.25rem 1rem;
  font-size: 15px;
  line-height: 1.75;
  color: #555555;
  font-family: Montserrat, sans-serif;
  background: #fafafa;
`;
